/*
 * Julvärmen cart: the slide-out cart <jv-cart-drawer> and the cart page <jv-main-cart>.
 *
 * Every change goes through the theme core (assets/jv-theme.js):
 *   JV.cart.change / JV.cart.update / JV.cart.add  -> the core re-renders every [data-jv-cart-section]
 *   by swapping its [data-jv-cart-region] parts, updates the cart counts and emits jv:cart:change.
 * Each section also checks that it really was re-rendered: its "footer"/"totals" region carries
 * data-jv-cart-token (item count, total, discount, line keys and quantities). When the token does not
 * match the cart in the jv:cart:change event, the section fetches itself through the Section Rendering
 * API and swaps its regions. So the cart stays right even if the core skips a section.
 * This file adds what shoppers feel: debounced steppers with optimistic quantities, a loading state on
 * the line, the remove animation, the order note, discount codes, add buttons for suggestions, and it
 * keeps the shopper's place (focus, caret, unsent typing, waiting quantities) across region swaps.
 */
(() => {
  'use strict';

  if (window.JVCart) return;
  const JV = window.JV;
  if (!JV || !JV.cart) return;

  const JVCart = (window.JVCart = { reopen: null });
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const QTY_DELAY = 400;
  const TEXT_DELAY = 700;
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const DRAWER_ID = 'JVCartDrawer';

  const motion = () => !reducedMotion.matches;
  const decode = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = String(value == null ? '' : value);
    return area.value;
  };
  const fill = (template, values = {}) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, name) => (name in values ? String(values[name]) : match));
  const sameCode = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  const cartType = () => (JV.config && JV.config.cartType) || 'drawer';

  // Same string as the Liquid capture "cart_token" in the cart sections.
  const cartToken = (cart) =>
    [
      cart.item_count,
      cart.total_price,
      cart.total_discount || 0,
      (cart.items || []).map((item) => `${item.key}:${item.quantity}`).join(','),
    ].join('|');

  /* ------------------------------------------------------------------ shared behaviour */
  class JVCartSection extends HTMLElement {
    connectedCallback() {
      if (!this._jvBound) {
        this._jvBound = true;
        this.init();
      }
      document.addEventListener('jv:cart:change', this.handlers.cartChange);
      window.addEventListener('pageshow', this.handlers.pageShow);
      this.observer.observe(this, { childList: true, subtree: true });
      this.classList.add('is-ready');
      this.connect();
    }

    disconnectedCallback() {
      document.removeEventListener('jv:cart:change', this.handlers.cartChange);
      window.removeEventListener('pageshow', this.handlers.pageShow);
      this.observer.disconnect();
      this.disconnect();
    }

    init() {
      this.sectionId = this.getAttribute('data-jv-cart-section');
      this.source = 'cart';
      this.strings = this.readStrings();
      this.live = this.querySelector('[data-jv-cart-live]');
      this.pending = new Map(); // line key -> quantity waiting to be saved (0 = removing)
      this.timers = new Map(); // line key -> debounce timer
      this.inflight = new Set(); // line keys with a request on the way
      this.textTimers = new Map(); // field key -> { timer, run } for the note
      this.drafts = new Map(); // focus key -> text typed that the server has not echoed back yet
      this.tasks = new Set();
      this.busyCount = 0;
      this.renderSeq = 0;
      this.focusState = null;
      this.focusRequest = null;
      this.keys = this.lineKeys();
      this.progressMessage = this.readProgressMessage();
      this.progressSeen = null;
      this.pendingNewKeys = new Set();

      this.handlers = {
        cartChange: (event) => this.onCartChange(event),
        pageShow: (event) => this.onPageShow(event),
      };
      this.observer = new MutationObserver((records) => this.onMutations(records));

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('focusin', (event) => this.onFocusIn(event));
      this.addEventListener('focusout', (event) => this.onFocusOut(event));
      this.setup();
    }

    /* Hooks for the two elements. */
    setup() {}
    connect() {}
    disconnect() {}
    cartChanged() {}
    rendered() {}
    isShown() {
      return true;
    }
    canFocus() {
      return true;
    }
    fallbackFocus() {
      return null;
    }
    // Where focus goes after an add button inside the section disappeared (its product is now in the cart).
    addedFocus(line) {
      return { target: line.querySelector('[data-jv-focus-key^="title-"]'), scroll: true };
    }

    readStrings() {
      const script = this.querySelector('script[data-jv-cart-strings]');
      let data = {};
      try {
        data = JSON.parse(script ? script.textContent : '{}') || {};
      } catch (error) {
        data = {};
      }
      const strings = {};
      Object.keys(data).forEach((key) => {
        strings[key] = decode(data[key]);
      });
      strings.error = strings.error || JV.t('error', 'Något gick fel. Försök igen.');
      return strings;
    }

    /* ---------------------------------------------------------------- lookups */
    lines() {
      return [...this.querySelectorAll('[data-jv-line]')];
    }

    lineKeys() {
      return new Set(this.lines().map((line) => line.dataset.key));
    }

    lineByKey(key) {
      return this.lines().find((line) => line.dataset.key === key) || null;
    }

    visibleLines() {
      return this.lines().filter((line) => !line.hidden && this.pending.get(line.dataset.key) !== 0);
    }

    byFocusKey(key) {
      return key ? this.querySelector(`[data-jv-focus-key="${CSS.escape(key)}"]`) : null;
    }

    readProgressMessage() {
      const message = this.querySelector('[data-jv-progress-msg]');
      return message ? message.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    focusKeyOf(element) {
      return element && element.getAttribute ? element.getAttribute('data-jv-focus-key') : null;
    }

    // A quantity typed into a field that has not been confirmed yet (no change event) also counts.
    qtyDraftKeys() {
      return [...this.drafts.keys()].filter((key) => key.startsWith('qty-'));
    }

    hasPending() {
      return (
        this.timers.size > 0 ||
        this.textTimers.size > 0 ||
        this.tasks.size > 0 ||
        this.pending.size > 0 ||
        this.qtyDraftKeys().length > 0
      );
    }

    /* ---------------------------------------------------------------- re-rendering */
    // The core re-renders cart sections before it emits jv:cart:change. If this section was not
    // re-rendered (its token still shows the old cart), fetch it and swap the regions here.
    ensureRendered(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const holder = this.querySelector('[data-jv-cart-token]');
      if (holder && holder.getAttribute('data-jv-cart-token') === cartToken(cart)) return;
      this.selfRender();
    }

    async selfRender() {
      if (!this.sectionId) return;
      this.renderSeq += 1;
      const seq = this.renderSeq;
      try {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        if (seq !== this.renderSeq || !data) return; // a newer render is on its way
        this.swapRegions(data[this.sectionId]);
      } catch (error) {
        /* the next cart change tries again */
      }
    }

    swapRegions(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-jv-cart-section="${CSS.escape(this.sectionId)}"]`);
      if (!fresh) return;
      this.querySelectorAll('[data-jv-cart-region]').forEach((region) => {
        const name = region.getAttribute('data-jv-cart-region');
        const replacement = fresh.querySelector(`[data-jv-cart-region="${CSS.escape(name)}"]`);
        if (replacement) region.replaceWith(document.importNode(replacement, true));
      });
      if (typeof JV.reveal === 'function') JV.reveal(this);
    }

    /* ---------------------------------------------------------------- events */
    onClick(event) {
      const target = event.target;

      const step = target.closest('[data-jv-line-step]');
      if (step && this.contains(step)) {
        const input = step.closest('[data-jv-line-qty-wrap]')?.querySelector('[data-jv-line-qty]');
        if (input) this.onStep(step, input, event);
        return;
      }

      const remove = target.closest('[data-jv-line-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.closest('[data-jv-line]');
        if (line) this.removeLine(line);
        return;
      }

      const add = target.closest('[data-jv-cart-add], [data-jv-add-variant]');
      if (add && this.contains(add)) {
        // Handled here (not by the core's document listener) so the button adds exactly once.
        event.preventDefault();
        event.stopPropagation();
        this.addVariant(add);
        return;
      }

      const codeRemove = target.closest('[data-jv-code-remove]');
      if (codeRemove) {
        event.preventDefault();
        this.removeCode(codeRemove.getAttribute('data-jv-code-remove'));
        return;
      }

      // Leaving the cart while a change is still waiting: save it first.
      const link = target.closest('a[href]');
      if (
        link &&
        this.hasPending() &&
        !link.target &&
        !event.defaultPrevented &&
        event.button === 0 &&
        !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        event.preventDefault();
        this.flush().finally(() => {
          window.location.href = link.href;
        });
      }
    }

    onChange(event) {
      const field = event.target;
      if (field.matches('[data-jv-line-qty]')) this.onQuantity(field);
    }

    onInput(event) {
      const field = event.target;
      const key = this.focusKeyOf(field);
      if (field.matches('[data-jv-line-qty]')) {
        if (key) this.drafts.set(key, field.value);
      } else if (field.matches('[data-jv-cart-note-input]')) {
        this.setStatus(field, '');
        this.queueText('note', () => this.saveNote(field));
      }
    }

    onKeydown(event) {
      if (event.key === 'Enter' && event.target.matches('[data-jv-line-qty]')) {
        event.preventDefault();
        this.onQuantity(event.target);
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-jv-code-form]')) {
        event.preventDefault();
        this.applyCode(form);
        return;
      }
      if (!form.matches('[data-jv-cart-form]')) return;

      const submitter = event.submitter || null;
      if (submitter && submitter.name === 'update') {
        // With JS every change saves itself; "Update" only exists for the no-JS form.
        event.preventDefault();
        this.flush();
        return;
      }
      if (this.submitting) return;
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      if (!this.hasPending()) return;

      // Save waiting changes first so checkout gets exactly what the shopper sees.
      event.preventDefault();
      const formId = form.id;
      this.flush().then(() => {
        const liveForm = (formId && document.getElementById(formId)) || form;
        if (submitter && submitter.name) {
          const field = document.createElement('input');
          field.type = 'hidden';
          field.name = submitter.name;
          field.value = submitter.value || '';
          liveForm.appendChild(field);
        }
        this.submitting = true;
        HTMLFormElement.prototype.submit.call(liveForm);
      });
    }

    onFocusIn(event) {
      const field = event.target.closest ? event.target.closest('[data-jv-focus-key]') : null;
      this.focusState = field ? { key: this.focusKeyOf(field), el: field } : null;
    }

    onFocusOut(event) {
      const field = event.target;
      // Leaving a quantity field that still holds a typed, unsaved number saves it.
      if (field && field.matches && field.matches('[data-jv-line-qty]') && this.focusKeyOf(field)) {
        const key = this.focusKeyOf(field);
        window.setTimeout(() => {
          if (!this.drafts.has(key)) return;
          const fresh = this.byFocusKey(key);
          if (!fresh) {
            this.drafts.delete(key);
            return;
          }
          if (document.activeElement === fresh) return;
          this.onQuantity(fresh);
        }, 0);
      }

      const state = this.focusState;
      if (!state || state.el !== event.target) return;
      window.setTimeout(() => {
        if (this.focusState === state && state.el.isConnected && document.activeElement !== state.el) {
          this.focusState = null;
        }
      }, 0);
    }

    onPageShow() {
      // Back from checkout (bfcache): the buttons must not stay in their loading state.
      this.submitting = false;
      this.querySelectorAll('[data-jv-cart-form] [aria-busy], button[form][aria-busy]').forEach((button) => {
        button.removeAttribute('aria-busy');
      });
    }

    onCartChange(event) {
      const detail = event.detail || {};
      const cart = detail.cart;
      if (!cart) return;
      const notePending = this.textTimers.has('note');
      this.querySelectorAll('[data-jv-cart-note-input]').forEach((field) => {
        if (!notePending && document.activeElement !== field && typeof cart.note === 'string' && field.value !== cart.note) {
          field.value = cart.note;
        }
      });
      const noteWrap = this.querySelector('[data-jv-cart-note-wrap]');
      if (noteWrap) noteWrap.hidden = !(cart.item_count > 0);
      this.cartChanged(cart, detail);
      if (!this.skipEnsure) this.ensureRendered(cart);
    }

    /* ---------------------------------------------------------------- after a region swap */
    onMutations(records) {
      const swapped = records.some((record) =>
        [...record.addedNodes].some((node) => node.nodeType === 1 && node.hasAttribute('data-jv-cart-region'))
      );
      if (swapped) this.afterRender();
    }

    afterRender() {
      this.reapplyPending();
      this.restoreDrafts();
      this.restoreFocus();
      this.syncBusy();
      this.animateNewLines();
      this.trackProgress();
      this.rendered();
    }

    reapplyPending() {
      this.pending.forEach((quantity, key) => {
        const line = this.lineByKey(key);
        if (!line) return;
        const input = line.querySelector('[data-jv-line-qty]');
        if (input) input.value = String(quantity);
        if (quantity === 0) {
          line.hidden = true;
          return;
        }
        this.refreshStepper(line, quantity);
      });
    }

    restoreDrafts() {
      this.drafts.forEach((value, key) => {
        const field = this.byFocusKey(key);
        if (field && 'value' in field && field.value !== value) field.value = value;
      });
    }

    restoreFocus() {
      const active = document.activeElement;
      const lost = !active || active === document.body || active === document.documentElement || active.tagName === 'DIALOG';

      // One-off requests: "focus the line that was just added" and "the cart is now empty".
      const request = this.focusRequest;
      if (request && Date.now() - request.at > 15000) {
        this.focusRequest = null;
      } else if (request && this.canFocus()) {
        let target = null;
        let scroll = false;
        if (request.type === 'variant' && lost) {
          const line = this.lines().find((item) => item.dataset.variantId === request.id);
          if (line) ({ target, scroll } = this.addedFocus(line));
        } else if (request.type === 'empty' && !this.visibleLines().length && (lost || active === this.fallbackFocus())) {
          target = this.querySelector('[data-jv-cart-empty-focus]');
        }
        if (target) {
          this.focusRequest = null;
          target.focus({ preventScroll: !scroll });
          return;
        }
      }

      // Otherwise: the focused control was swapped out, so focus its fresh copy (same data-jv-focus-key).
      const state = this.focusState;
      if (!lost || !state || state.el.isConnected || !this.canFocus()) return;
      const field = this.byFocusKey(state.key);
      if (!field) return;
      let start = null;
      let end = null;
      try {
        start = state.el.selectionStart;
        end = state.el.selectionEnd;
      } catch (error) {
        start = null;
      }
      field.focus({ preventScroll: true });
      if (start !== null && typeof field.setSelectionRange === 'function') {
        try {
          field.setSelectionRange(start, end);
        } catch (error) {
          /* number inputs have no caret */
        }
      }
      this.focusState = { key: state.key, el: field };
    }

    syncBusy() {
      this.lines().forEach((line) => {
        const busy = this.pending.has(line.dataset.key);
        line.classList.toggle('is-busy', busy);
        if (busy) line.setAttribute('aria-busy', 'true');
        else line.removeAttribute('aria-busy');
      });
      const working = this.pending.size > 0 || this.busyCount > 0;
      this.querySelectorAll('[data-jv-cart-totals]').forEach((element) => element.classList.toggle('is-busy', working));
    }

    busy(delta) {
      this.busyCount = Math.max(0, this.busyCount + delta);
      this.syncBusy();
    }

    animateNewLines() {
      const keys = this.lineKeys();
      const fresh = [...keys].filter((key) => !this.keys.has(key));
      this.keys = keys;
      if (!fresh.length) return;
      if (!this.isShown()) {
        fresh.forEach((key) => this.pendingNewKeys.add(key));
        return;
      }
      fresh.forEach((key) => this.enterLine(this.lineByKey(key)));
    }

    playNewLines() {
      this.pendingNewKeys.forEach((key) => this.enterLine(this.lineByKey(key)));
      this.pendingNewKeys.clear();
    }

    enterLine(line) {
      if (!line || !motion() || typeof line.animate !== 'function') return;
      line.animate(
        [
          { opacity: 0, transform: 'translateY(-10px)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 460, easing: EASE }
      );
    }

    trackProgress() {
      const bar = this.querySelector('[data-jv-progress]');
      const value = bar ? parseFloat(bar.getAttribute('data-jv-progress')) || 0 : null;
      if (!this.isShown()) return; // played when the drawer opens
      this.animateBar(bar, this.progressSeen, value);
      this.progressSeen = value;
    }

    playProgress() {
      const bar = this.querySelector('[data-jv-progress]');
      const value = bar ? parseFloat(bar.getAttribute('data-jv-progress')) || 0 : null;
      this.animateBar(bar, this.progressSeen === null ? 0 : this.progressSeen, value);
      this.progressSeen = value;
    }

    animateBar(bar, from, to) {
      if (!bar || from === null || to === null || from === to || !motion() || typeof bar.animate !== 'function') return;
      bar.animate([{ width: `${from}%` }, { width: `${to}%` }], { duration: 700, easing: EASE });
    }

    /* ---------------------------------------------------------------- adding suggestions */
    async addVariant(button) {
      if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
      const id = Number(button.getAttribute('data-jv-cart-add') || button.getAttribute('data-jv-add-variant'));
      if (!id) return;
      const source = button.getAttribute('data-jv-cart-source') || button.getAttribute('data-jv-source') || this.source;
      this.focusRequest = { type: 'variant', id: String(id), at: Date.now() };
      JV.setBusy(button, true);
      const task = (async () => {
        this.busy(1);
        try {
          const cart = await JV.cart.add([{ id, quantity: 1 }], { openDrawer: false, source });
          const item = (cart.items || []).find((entry) => Number(entry.variant_id) === id);
          const title = item ? item.product_title || item.title || '' : '';
          this.added(title);
        } catch (error) {
          this.fail(error);
        } finally {
          if (button.isConnected) JV.setBusy(button, false);
          this.busy(-1);
        }
      })();
      return this.track(task);
    }

    added(title) {
      this.announce(title ? fill(this.strings.added, { title }) : this.strings.addedShort, true);
    }

    /* ---------------------------------------------------------------- quantities */
    refreshStepper(line, quantity) {
      const input = line.querySelector('[data-jv-line-qty]');
      const plus = line.querySelector('.jv-cart-line__plus');
      if (!input || !plus) return;
      if (input.max !== '' && quantity >= Number(input.max)) plus.setAttribute('aria-disabled', 'true');
      else plus.removeAttribute('aria-disabled');
    }

    // "−" and "+" on a cart line. "+" is ignored at the stock limit, "−" always takes exactly one step down,
    // so a line that is above the stock left is never pushed down by the cap.
    onStep(step, input, event) {
      event.preventDefault();
      event.stopPropagation();
      const delta = Number(step.getAttribute('data-jv-line-step')) || 0;
      if (!delta) return;
      const from = Math.max(0, Math.floor(Number(input.value)) || 0);
      const max = input.max !== '' ? Number(input.max) : Infinity;
      let next = from + delta;
      if (delta > 0) {
        if (step.getAttribute('aria-disabled') === 'true' || from >= max) {
          this.notifyMax(max);
          return;
        }
        next = Math.min(max, next);
      } else {
        next = Math.max(0, next);
      }
      if (next === from) return;
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onQuantity(input) {
      const line = input.closest('[data-jv-line]');
      if (!line) return;
      const key = line.dataset.key;
      const focusKey = this.focusKeyOf(input);
      if (focusKey) this.drafts.delete(focusKey);

      const server = Number(line.dataset.quantity) || 0;
      const current = this.pending.has(key) ? this.pending.get(key) : server;
      let quantity = Math.floor(Number(input.value));
      if (input.value === '' || !Number.isFinite(quantity) || quantity < 0) {
        input.value = String(current);
        return;
      }
      const max = input.max !== '' ? Number(input.max) : Infinity;
      if (quantity > max && quantity > current) {
        quantity = Math.max(max, current);
        input.value = String(quantity);
        this.notifyMax(max);
      }
      if (quantity === 0) {
        this.removeLine(line);
        return;
      }
      this.refreshStepper(line, quantity);
      if (quantity === current) return;

      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
      if (quantity === server && !this.inflight.has(key)) {
        this.pending.delete(key);
        this.syncBusy();
        return;
      }
      this.pending.set(key, quantity);
      this.syncBusy();
      this.timers.set(
        key,
        window.setTimeout(() => this.commit(key), QTY_DELAY)
      );
    }

    commit(key) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
      if (!this.pending.has(key)) return Promise.resolve();
      const quantity = this.pending.get(key);
      const line = this.lineByKey(key);
      const title = line ? line.dataset.title : '';

      const task = (async () => {
        this.inflight.add(key);
        this.busy(1);
        try {
          const cart = await JV.cart.change({ id: key, quantity }, { source: this.source });
          if (this.pending.get(key) === quantity) this.pending.delete(key);
          if (quantity === 0) {
            this.announce(fill(this.strings.removed, { title }), true);
          } else {
            const item = (cart.items || []).find((entry) => entry.key === key);
            if (item && item.quantity < quantity) this.notifyMax(item.quantity);
            else this.announce(fill(this.strings.quantityUpdated, { title, quantity }), true);
          }
        } catch (error) {
          if (this.pending.get(key) === quantity) this.pending.delete(key);
          this.fail(error);
          this.restoreLine(key);
        } finally {
          this.inflight.delete(key);
          this.settleLine(key);
          this.busy(-1);
        }
      })();
      return this.track(task);
    }

    settleLine(key) {
      if (this.pending.has(key)) return;
      const line = this.lineByKey(key);
      if (!line || line.hidden) return;
      const input = line.querySelector('[data-jv-line-qty]');
      if (input && document.activeElement !== input) input.value = line.dataset.quantity;
      this.refreshStepper(line, Number(line.dataset.quantity) || 0);
    }

    restoreLine(key) {
      const line = this.lineByKey(key);
      if (!line) return;
      if (line._jvCollapse) {
        line._jvCollapse.cancel();
        line._jvCollapse = null;
      }
      line.style.overflow = '';
      if (line.hidden) {
        line.hidden = false;
        if (motion() && typeof line.animate === 'function') {
          line.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
        }
      }
      const input = line.querySelector('[data-jv-line-qty]');
      if (input) input.value = line.dataset.quantity;
    }

    removeLine(line) {
      const key = line.dataset.key;
      if (this.pending.get(key) === 0) return Promise.resolve();
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);

      const lines = this.visibleLines();
      const index = lines.indexOf(line);
      const next = lines[index + 1] || lines[index - 1] || null;
      const hadFocus = line.contains(document.activeElement);

      this.pending.set(key, 0);
      // On the cart page this field is updates[<line key>]: if the form is sent now, the line still goes.
      const input = line.querySelector('[data-jv-line-qty]');
      if (input) input.value = '0';
      this.syncBusy();

      if (hadFocus) {
        // Keep keyboard users in the list: move to the neighbouring line before this one goes.
        const target = next ? next.querySelector('[data-jv-focus-key^="title-"]') : this.fallbackFocus();
        if (target) target.focus({ preventScroll: true });
        if (!next) this.focusRequest = { type: 'empty', at: Date.now() };
      }

      const task = (async () => {
        await this.collapse(line);
        if (this.pending.get(key) === 0 && line.isConnected) line.hidden = true;
        await this.commit(key);
      })();
      return this.track(task);
    }

    collapse(line) {
      if (!motion() || typeof line.animate !== 'function') return Promise.resolve();
      const height = line.getBoundingClientRect().height;
      line.style.overflow = 'hidden';
      const animation = line.animate(
        [
          { height: `${height}px`, opacity: 1, transform: 'translateX(0)' },
          { opacity: 0, offset: 0.45 },
          {
            height: '0px',
            opacity: 0,
            paddingTop: '0px',
            paddingBottom: '0px',
            borderBottomWidth: '0px',
            transform: 'translateX(16px)',
          },
        ],
        { duration: 340, easing: EASE, fill: 'forwards' }
      );
      line._jvCollapse = animation;
      return animation.finished.catch(() => {});
    }

    notifyMax(quantity) {
      const message = quantity > 0 || !this.strings.soldOut ? fill(this.strings.maxQuantity, { quantity }) : this.strings.soldOut;
      JV.toast(message, { tone: 'error' });
    }

    fail(error) {
      JV.toast((error && error.message) || this.strings.error, { tone: 'error' });
    }

    /* ---------------------------------------------------------------- order note */
    queueText(key, run) {
      const queued = this.textTimers.get(key);
      if (queued) clearTimeout(queued.timer);
      const timer = window.setTimeout(() => {
        this.textTimers.delete(key);
        run();
      }, TEXT_DELAY);
      this.textTimers.set(key, { timer, run });
    }

    saveNote(field) {
      const value = field.value;
      const task = (async () => {
        try {
          await JV.cart.update({ note: value }, { source: `${this.source}-note` });
          if (field.value === value) this.setStatus(field, this.strings.saved);
        } catch (error) {
          this.fail(error);
        }
      })();
      return this.track(task);
    }

    setStatus(field, text) {
      const status = field.parentElement ? field.parentElement.querySelector('[data-jv-note-status]') : null;
      if (!status) return;
      window.clearTimeout(status._jvTimer);
      status.textContent = text;
      if (text) {
        status._jvTimer = window.setTimeout(() => {
          status.textContent = '';
        }, 2400);
      }
    }

    /* ---------------------------------------------------------------- saving before leaving */
    track(promise) {
      this.tasks.add(promise);
      promise
        .catch(() => {})
        .finally(() => {
          this.tasks.delete(promise);
        });
      return promise;
    }

    async flush() {
      this.qtyDraftKeys().forEach((key) => {
        const field = this.byFocusKey(key);
        if (field) this.onQuantity(field);
        else this.drafts.delete(key);
      });
      [...this.timers.keys()].forEach((key) => this.commit(key));
      [...this.textTimers.entries()].forEach(([key, queued]) => {
        clearTimeout(queued.timer);
        this.textTimers.delete(key);
        queued.run();
      });
      let rounds = 0;
      while (this.tasks.size && rounds < 20) {
        rounds += 1;
        // eslint-disable-next-line no-await-in-loop
        await Promise.allSettled([...this.tasks]);
      }
    }

    /* ---------------------------------------------------------------- discount codes (cart page) */
    applyCode() {
      return Promise.resolve();
    }

    removeCode() {
      return Promise.resolve();
    }

    /* ---------------------------------------------------------------- live region */
    announce(message, withTotals = false) {
      if (!this.live) return;
      let text = message || '';
      if (withTotals) {
        const total = this.querySelector('[data-jv-cart-total]');
        if (total && this.strings.subtotalNow) text += ` ${fill(this.strings.subtotalNow, { amount: total.textContent.trim() })}`;
        const progress = this.readProgressMessage();
        if (progress && progress !== this.progressMessage) text += ` ${progress}`;
      }
      this.progressMessage = this.readProgressMessage();
      this.live.textContent = '';
      window.clearTimeout(this.liveTimer);
      this.liveTimer = window.setTimeout(() => {
        this.live.textContent = text.trim();
      }, 80);
    }
  }

  /* ------------------------------------------------------------------ slide-out cart */
  class JVCartDrawer extends JVCartSection {
    setup() {
      this.source = 'cart-drawer';
      this.dialog = this.querySelector('dialog');
      this.scroller = this.querySelector('[data-jv-cart-scroll]');
      this.scrollTopOnOpen = false;
      this.lastAddAt = 0;
      this.onOpenRequest = (event) => this.open(event.detail || {});
      this.addEventListener('jv:dialog:open', (event) => {
        if (event.target === this.dialog) this.opened();
      });
      this.addEventListener('jv:dialog:close', (event) => {
        if (event.target === this.dialog) this.closed();
      });
    }

    connect() {
      document.addEventListener('jv:cart:open', this.onOpenRequest);
      if (designMode && JVCart.reopen === this.sectionId && this.dialog) {
        JVCart.reopen = null;
        window.requestAnimationFrame(() => JV.openDialog(this.dialog));
      }
    }

    disconnect() {
      document.removeEventListener('jv:cart:open', this.onOpenRequest);
      if (this.dialog && this.dialog.open) {
        if (designMode) JVCart.reopen = this.sectionId;
        JV.closeDialog(this.dialog);
      }
    }

    isShown() {
      return Boolean(this.dialog && this.dialog.open);
    }

    canFocus() {
      return this.isShown();
    }

    fallbackFocus() {
      return this.querySelector('#JVCartDrawerTitle');
    }

    open(detail = {}) {
      // On the cart page the page itself is the cart; adding there updates it in place.
      if (!this.dialog || document.querySelector('jv-main-cart')) return;
      const source = String(detail.source || '');
      if (cartType() === 'toast' && Date.now() - this.lastAddAt < 2000 && source !== 'header' && source !== 'standard-action') {
        JV.toast(this.strings.addedShort || JV.t('added', ''));
        return;
      }
      JV.openDialog(this.dialog);
    }

    opened() {
      // Start at the heading ("Varukorg, 2 varor"); Tab then reaches the close button and the lines.
      const title = this.fallbackFocus();
      if (title) title.focus({ preventScroll: true });
      if (this.scrollTopOnOpen && this.scroller) this.scroller.scrollTop = 0;
      this.scrollTopOnOpen = false;
      this.playProgress();
      this.playNewLines();
    }

    closed() {}

    cartChanged(cart, detail) {
      const source = String(detail.source || '');
      if (detail.action === 'add') {
        this.lastAddAt = Date.now();
        if (!source.startsWith('cart-drawer')) this.scrollTopOnOpen = true;
      }
    }
  }

  /* ------------------------------------------------------------------ cart page */
  class JVMainCart extends JVCartSection {
    setup() {
      this.source = 'cart-page';
      this.progressSeen = 0;
      window.requestAnimationFrame(() => this.playProgress());
    }

    // On the page the shopper is down at the suggestions: stay there and move to the next suggestion
    // instead of jumping up to the new line (the toast confirms the add).
    addedFocus(line) {
      const next = this.querySelector('[data-jv-cart-region="upsells"] [data-jv-add-variant], [data-jv-cart-region="upsells"] a[href]');
      return { target: next || line.querySelector('[data-jv-focus-key^="title-"]'), scroll: false };
    }

    added(title) {
      super.added(title);
      JV.toast(title ? fill(this.strings.added, { title }) : this.strings.addedShort);
    }

    cartChanged(cart) {
      const empty = !(cart.item_count > 0);
      if (String(empty) !== this.dataset.empty) {
        // Going from empty to filled (or back) changes the whole page: load it fresh.
        this.skipEnsure = true;
        if (!this.reloading) {
          this.reloading = true;
          window.location.reload();
        }
        return;
      }
      this.skipEnsure = false;
    }

    // The codes to keep when the code list is sent again. A code Shopify rejected (applicable: false)
    // stays in the cart's list until the list is replaced, so it is left out here.
    codesInCart(cart) {
      const fromCart = (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
        .filter((entry) => entry && entry.code && entry.applicable !== false)
        .map((entry) => entry.code);
      const fromPage = [...this.querySelectorAll('[data-jv-code-remove]')]
        .map((button) => button.getAttribute('data-jv-code-remove'))
        .filter(Boolean);
      const codes = [];
      [...fromCart, ...fromPage].forEach((code) => {
        if (!codes.some((known) => sameCode(known, code))) codes.push(code);
      });
      return codes;
    }

    codeMessage(text, isError = false) {
      const message = this.querySelector('[data-jv-code-msg]');
      const input = this.querySelector('[data-jv-code-input]');
      if (message) {
        message.textContent = text || '';
        message.classList.toggle('is-error', Boolean(isError && text));
      }
      if (input) {
        if (isError && text) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    applyCode(form) {
      const input = form.querySelector('[data-jv-code-input]');
      const button = form.querySelector('[type="submit"]');
      const code = input ? input.value.trim() : '';
      if (!code) {
        this.codeMessage(this.strings.discountEmpty, true);
        if (input) input.focus();
        return Promise.resolve();
      }
      this.codeMessage('');
      if (button) JV.setBusy(button, true);

      const task = (async () => {
        this.busy(1);
        try {
          const current = await JV.cart.get();
          const codes = this.codesInCart(current);
          if (codes.some((known) => sameCode(known, code))) {
            input.value = '';
            this.codeMessage(fill(this.strings.discountApplied, { code }));
            return;
          }
          const cart = await JV.cart.update({ discount: [...codes, code].join(',') }, { source: 'cart-discount' });
          const entry = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).find((item) => item && sameCode(item.code, code));
          if (!entry || entry.applicable === false) {
            if (entry) await JV.cart.update({ discount: codes.join(',') }, { source: 'cart-discount' });
            this.codeMessage(fill(this.strings.discountInvalid, { code }), true);
            return;
          }
          // A code can change what is shown without changing the totals (free shipping): render to be sure.
          await this.selfRender();
          const shown = [...this.querySelectorAll('[data-jv-code-remove]')].some((pill) => sameCode(pill.getAttribute('data-jv-code-remove'), code));
          input.value = '';
          this.codeMessage(fill(shown ? this.strings.discountApplied : this.strings.discountShipping, { code }));
        } catch (error) {
          this.codeMessage((error && error.message) || this.strings.error, true);
        } finally {
          if (button) JV.setBusy(button, false);
          this.busy(-1);
        }
      })();
      return this.track(task);
    }

    removeCode(code) {
      const task = (async () => {
        this.busy(1);
        try {
          const current = await JV.cart.get();
          const remaining = this.codesInCart(current).filter((known) => !sameCode(known, code));
          await JV.cart.update({ discount: remaining.join(',') }, { source: 'cart-discount' });
          await this.selfRender();
          this.codeMessage(fill(this.strings.discountRemoved, { code }));
          const input = this.querySelector('[data-jv-code-input]');
          const active = document.activeElement;
          if (input && (!active || active === document.body)) input.focus({ preventScroll: true });
        } catch (error) {
          this.fail(error);
        } finally {
          this.busy(-1);
        }
      })();
      return this.track(task);
    }
  }

  if (!customElements.get('jv-cart-drawer')) customElements.define('jv-cart-drawer', JVCartDrawer);
  if (!customElements.get('jv-main-cart')) customElements.define('jv-main-cart', JVMainCart);

  /* ------------------------------------------------------------------ openers */
  // The header's cart button: <a href="/cart" data-jv-cart-open> (or data-jv-open="JVCartDrawer").
  // With "Cart type: page" the link simply goes to the cart page.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('[data-jv-cart-open], [data-jv-open="' + DRAWER_ID + '"]');
    if (!opener || cartType() === 'page') return;
    if (!document.getElementById(DRAWER_ID) || document.querySelector('jv-main-cart')) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    JV.cart.open({ source: 'header' });
  });

  // A page restored from the back/forward cache may show an old cart (for example after checkout).
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && document.querySelector('[data-jv-cart-section]')) JV.cart.refresh().catch(() => {});
  });
})();
